import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronLeft, Youtube, Play, Clock } from 'lucide-react';
import SiteHeaderV2 from '@/components/website/SiteHeaderV2';
import SiteFooterV2 from '@/components/website/SiteFooterV2';
import PageHeaderV2 from '@/components/website/PageHeaderV2';

/* eslint-disable react/no-unescaped-entities */

const YouTubePage = () => {
  const videos = [
    {
      id: "O61hmtfGXfI",
      title: "Améliorer sa tolérance au CO2 à l'effort : exercice 1",
      duration: "4:18",
      views: "94",
      publishedTime: "Il y a 2 mois"
    },
    {
      id: "q4YCmb4SAGo",
      title: "Technique pour désengager les épaules et ressentir la respiration abdominale",
      duration: "3:03",
      views: "55",
      publishedTime: "Il y a 2 mois"
    },
    {
      id: "WAJkNpz1Lg0",
      title: "La mécanique respiratoire optimale",
      duration: "3:32",
      views: "21",
      publishedTime: "Il y a 2 mois"
    },
    {
      id: "NHQPCnTPWC0",
      title: "Exercices de mobilisation de la colonne et relâchement dans le mouvement (wudang Qi gong)",
      duration: "16:52",
      views: "80",
      publishedTime: "Il y a 10 mois"
    },
    {
      id: "o79oewGbmFY",
      title: "Échauffement série Qi Gong",
      duration: "11:37",
      views: "67",
      publishedTime: "Il y a 10 mois"
    },
    {
      id: "hfxVOI_WjYU",
      title: "Utiliser la respiration pour améliorer sa mobilité",
      duration: "3:41",
      views: "95",
      publishedTime: "Il y a 1 an"
    }
  ];

  const playlists = [
    {
      title: "Ressources pour la pratique respiratoire",
      videoCount: 38,
      firstVideoId: "PXewT9lnOhM",
      playlistId: "PLM1Xo_tNlIgQst_Tbuxx5yoipzNkMDGZy"
    },
    {
      title: "Exercices respiratoires",
      videoCount: 11,
      firstVideoId: "aQ1rb2sCTZw",
      playlistId: "PLM1Xo_tNlIgTNzJAyzs8WFyYdiqiVlCom"
    },
    {
      title: "Physio respiratoire Qi Gong",
      videoCount: 5,
      firstVideoId: "0R0E3Il7DIU",
      playlistId: "PLM1Xo_tNlIgQJVRX0BuhaY1Zo9I2xeDe1"
    },
    {
      title: "Diaphragme",
      videoCount: 2,
      firstVideoId: "2WD5a7cFTGo",
      playlistId: "PLM1Xo_tNlIgTWwRugRDYekRfJph4o0BSZ"
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FFFFFF", fontFamily: "Figtree, sans-serif" }}>
      <SiteHeaderV2 />
      
      {/* Page Header Image */}
      <PageHeaderV2 
        title="Ma Chaîne YouTube" 
        subtitle="Vidéos pour la pratique respiratoire à domicile"
      />
      
      {/* Hero */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-red-600 to-rose-600 flex items-center justify-center">
              <Youtube className="w-8 h-8 text-white" />
            </div>
            <div>
              <a 
                href="https://www.youtube.com/@Jaccardjphysio" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sky-600 hover:text-sky-700 font-semibold flex items-center gap-2 mt-2"
              >
                @Jaccardjphysio
                <Youtube size={18} />
              </a>
            </div>
          </div>
          
          <div className="prose prose-lg max-w-none text-slate-600">
            <p className="text-xl leading-relaxed mb-4">
              Cette chaîne est à l'attention des patients atteints de COVID long que je traite au quotidien.
            </p>
            <p className="leading-relaxed mb-3">
              Les exercices présentés dans ces vidéos sont des aides pour la pratique à domicile des exercices 
              vus en séance. Les consignes sont extrêmement importantes pour comprendre la portée de chaque pratique, 
              dont les objectifs majeurs sont :
            </p>
            <ol className="space-y-2 ml-6">
              <li>
                <strong>Le contrôle de la respiration</strong> : prise de conscience et régulation de l'hyperventilation 
                et de ses conséquences sur l'organisme
              </li>
              <li>
                <strong>La régulation du système nerveux autonome</strong> : permettre au corps le retour à l'homéostasie
              </li>
              <li>
                <strong>La perception du fonctionnement automatique</strong> : conscientisation des tensions et apprentissage 
                de techniques pour vivre dans un corps détendu, mobile, souple et économe en énergie
              </li>
            </ol>
          </div>

          <div className="mt-6 p-4 border-l-4 border-sky-400 rounded-lg">
            <p className="text-slate-900 font-semibold">
               Important : Ces exercices ne doivent être pratiqués qu'après évaluation d'un kinésithérapeute et/ou avis médical.
            </p>
          </div>
        </div>
      </section>

      {/* Latest Videos */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl mb-8" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
            Dernières Vidéos
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map((video, index) => (
              <Card 
                key={index} 
                className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group cursor-pointer"
              >
                <div className="relative">
                  {/* YouTube Thumbnail */}
                  <div className="aspect-video bg-slate-200 relative overflow-hidden">
                    <iframe
                      src={`https://www.youtube.com/embed/${video.id}`}
                      title={video.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                    />
                  </div>
                  
                  {/* Duration badge */}
                  <div className="absolute bottom-2 right-2 bg-black/80 text-white px-2 py-1 rounded text-xs font-semibold flex items-center gap-1">
                    <Clock size={12} />
                    {video.duration}
                  </div>
                </div>
                
                <CardContent className="p-4">
                  <h3 className="font-medium mb-2 line-clamp-2 group-hover:text-sky-600 transition-colors">
                    {video.title}
                  </h3>
                  <div className="flex items-center gap-3 text-sm text-slate-500">
                    <span>{video.views} vues</span>
                    <span>•</span>
                    <span>{video.publishedTime}</span>
                  </div>
                  <a
                    href={`https://www.youtube.com/watch?v=${video.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-2 text-sky-600 hover:text-sky-700 font-semibold text-sm"
                  >
                    <Play size={16} />
                    Regarder sur YouTube
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Playlists */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl mb-8" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
            Playlists Thématiques
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {playlists.map((playlist, index) => (
              <Card 
                key={index} 
                className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group"
              >
                <div className="flex gap-4 p-6">
                  <div className="relative flex-shrink-0">
                    <div className="w-40 h-24 bg-slate-200 rounded-lg overflow-hidden">
                      <img 
                        src={`https://img.youtube.com/vi/${playlist.firstVideoId}/mqdefault.jpg`}
                        alt={playlist.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded-lg">
                      <div className="text-white text-center">
                        <div className="text-2xl font-bold">{playlist.videoCount}</div>
                        <div className="text-xs">vidéos</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="font-medium mb-2 group-hover:text-sky-600 transition-colors">
                      {playlist.title}
                    </h3>
                    <a
                      href={`https://www.youtube.com/playlist?list=${playlist.playlistId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sky-600 hover:text-sky-700 font-semibold text-sm"
                    >
                      <Youtube size={16} />
                      Voir la playlist
                    </a>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Subscribe CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <Youtube className="w-16 h-16 text-white mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-medium mb-6" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
            Abonnez-vous à Ma Chaîne
          </h2>
          <p className="text-sky-100 text-lg mb-8 max-w-2xl mx-auto">
            Recevez les nouvelles vidéos d'exercices respiratoires et de techniques de rééducation 
            pour améliorer votre santé au quotidien.
          </p>
          <a
            href="https://www.youtube.com/@Jaccardjphysio?sub_confirmation=1"
            target="_blank"
            rel="noopener noreferrer"
          >
            <button className="px-10 py-4 text-lg rounded-full text-sky-600 hover:bg-slate-50 shadow-lg font-semibold transition-colors inline-flex items-center gap-3">
              <Youtube size={24} />
              S'abonner sur YouTube
            </button>
          </a>
        </div>
      </section>

      <SiteFooterV2 />
    </div>
  );
};

export default YouTubePage;
